import { ref, computed } from 'vue';
import { enTranslations } from '../locales/en';
import { ruTranslations } from '../locales/ru';
import { ukTranslations } from '../locales/uk';

export type Language = 'en' | 'ru' | 'uk' | 'pl' | 'de' | 'es';

const STORAGE_KEY = 'cafe-language';
const SOURCE_STORAGE_KEY = 'cafe-language-source';
const DEFAULT_LANGUAGE: Language = 'en';
const SUPPORTED: Language[] = ['en', 'ru', 'uk', 'pl', 'de', 'es'];

export const AVAILABLE_LANGUAGES: { code: Language; label: string }[] = [
  { code: 'en', label: 'EN' },
  { code: 'ru', label: 'RU' },
  { code: 'uk', label: 'UK' },
  { code: 'de', label: 'DE' },
  { code: 'pl', label: 'PL' },
  { code: 'es', label: 'ES' },
];

const loadedTranslations = ref<Record<Language, Record<string, string>>>({
  en: enTranslations,
  ru: ruTranslations,
  uk: ukTranslations,
  pl: enTranslations, // fallback until loaded
  de: enTranslations,
  es: enTranslations,
});

const dynamicLoaders: Partial<Record<Language, () => Promise<Record<string, string>>>> = {
  pl: () => import('../locales/pl').then(m => m.plTranslations),
  de: () => import('../locales/de').then(m => m.deTranslations),
  es: () => import('../locales/es').then(m => m.esTranslations),
};

async function ensureLocale(lang: Language) {
  const loader = dynamicLoaders[lang];
  if (!loader || loadedTranslations.value[lang] !== enTranslations) return;
  const dict = await loader();
  loadedTranslations.value = { ...loadedTranslations.value, [lang]: dict };
}

const normalizeLanguage = (value?: string | null): Language | null => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return null;

  for (const lang of SUPPORTED) {
    if (raw === lang || raw.startsWith(`${lang}-`)) return lang;
  }

  if (raw.startsWith('zh')) return null;
  if (raw.startsWith('be')) return 'ru';
  if (raw.startsWith('uk')) return 'uk';
  if (raw.startsWith('sr') || raw.startsWith('bg') || raw.startsWith('mk')) return 'ru';

  return null;
};

const getBrowserLanguage = (): Language => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const candidates = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language];

  for (const candidate of candidates) {
    const matched = normalizeLanguage(candidate);
    if (matched) return matched;
  }

  return DEFAULT_LANGUAGE;
};

// Reads a `?lang=` query parameter (used by guest table QR links so a customer's
// phone opens in the venue's language instead of the phone's browser default).
const getQueryLanguage = (): Language | null => {
  if (typeof window === 'undefined') return null;
  try {
    const value = new URLSearchParams(window.location.search).get('lang');
    return normalizeLanguage(value);
  } catch {
    return null;
  }
};

const getInitialLanguage = (): Language => {
  if (typeof window !== 'undefined') {
    const stored = normalizeLanguage(localStorage.getItem(STORAGE_KEY));
    const source = String(localStorage.getItem(SOURCE_STORAGE_KEY) || '').trim().toLowerCase();
    // A device's explicit manual choice always wins.
    if (stored && source === 'manual') return stored;
    // Otherwise a venue language carried in the URL (?lang=) wins over the browser default.
    const fromQuery = getQueryLanguage();
    if (fromQuery) return fromQuery;
    return getBrowserLanguage();
  }
  return DEFAULT_LANGUAGE;
};

const currentLanguage = ref<Language>(getInitialLanguage());

// Pre-fetch the initial language if it needs dynamic loading
ensureLocale(currentLanguage.value);

// The portal opens in the visitor's browser language everywhere (ru/uk included),
// so this regional geo-IP gate is intentionally disabled. Kept as a no-op so the
// existing call site in App.vue stays valid. An explicit manual choice or a venue
// language from the QR (?lang=) still win via getInitialLanguage().
export const applyRegionalLanguageDefault = async (): Promise<void> => {
  // No-op: browser language is the default in every region.
};

export const useLanguage = () => {
  const setLanguage = (lang: Language) => {
    currentLanguage.value = lang;
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lang);
      localStorage.setItem(SOURCE_STORAGE_KEY, 'manual');
      document.documentElement.lang = lang;
    }
    ensureLocale(lang);
  };

  const t = (key: string, fallback?: string): string => {
    const dict = loadedTranslations.value[currentLanguage.value];
    const enDict = loadedTranslations.value.en;
    return dict[key] || enDict[key] || fallback || key;
  };

  const language = computed(() => currentLanguage.value);

  return { language, setLanguage, t };
};

if (typeof document !== 'undefined') {
  document.documentElement.lang = currentLanguage.value;
}
